import { Check, Building, Package, Store, MessageCircle, UserPlus, ChevronRight } from "lucide-react";
import type { Mission } from "@/lib/api/controlPlaneOnboardingClient";

// ── Icon map ───────────────────────────────────────────────────────

export const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  building: Building,
  package: Package,
  store: Store,
  "message-circle": MessageCircle,
  "user-plus": UserPlus,
};

// ── Progress Ring ──────────────────────────────────────────────────

const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function ProgressRing({ progress }: { progress: number }) {
  const offset = RING_CIRCUMFERENCE * (1 - progress);
  return (
    <svg
      className="absolute inset-0 -rotate-90"
      width="48"
      height="48"
      viewBox="0 0 48 48"
    >
      <circle
        cx="24"
        cy="24"
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        className="text-gray-200 dark:text-gray-700"
      />
      <circle
        cx="24"
        cy="24"
        r={RING_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-blue-500 transition-[stroke-dashoffset] duration-600 ease-in-out"
      />
    </svg>
  );
}

// ── Skeleton Row ─────────────────────────────────────────────────

export function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 border-l-[3px] border-l-transparent animate-pulse">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="h-3.5 w-3/4 rounded-md bg-gray-200 dark:bg-gray-700" />
        <div className="h-3 w-1/2 rounded-md bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="flex-shrink-0 h-5 w-10 rounded-full bg-gray-100 dark:bg-gray-800" />
    </div>
  );
}

// ── Mission Row ────────────────────────────────────────────────────

export function MissionRow({
  mission,
  isNext,
  onClick,
}: {
  mission: Mission;
  isNext: boolean;
  onClick?: () => void;
}) {
  const IconComponent = ICON_MAP[mission.icon];
  const isClickable = !mission.completed && !!onClick;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3
        border-b border-gray-100 dark:border-gray-800 last:border-b-0
        ${isNext ? "border-l-[3px] border-l-blue-500" : "border-l-[3px] border-l-transparent"}
        ${mission.completed ? "opacity-60" : ""}
        ${isClickable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800" : ""}
      `}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : -1}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      {/* Status icon */}
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
        {mission.completed ? (
          <Check size={18} className="text-green-500" />
        ) : IconComponent ? (
          <IconComponent size={18} className="text-gray-400 dark:text-gray-500" />
        ) : (
          <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
        )}
      </div>

      {/* Title + description */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-semibold truncate ${
            mission.completed
              ? "text-gray-400 dark:text-gray-500"
              : "text-gray-900 dark:text-gray-100"
          }`}
        >
          {mission.title}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {mission.description}
        </p>
      </div>

      {/* Credit badge */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {mission.completed ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400">
            Earned
          </span>
        ) : (
          <>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
              +{mission.credits_reward}
            </span>
            <ChevronRight size={14} className="text-gray-400 dark:text-gray-500" />
          </>
        )}
      </div>
    </div>
  );
}
